import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api, { formatNGN } from "../lib/api";

export default function PublicationDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [pub, setPub] = useState(null);
    const [fmt, setFmt] = useState("hardcopy");
    const [qty, setQty] = useState(1);

    useEffect(() => {
        api.get(`/publications/${id}`).then(({ data }) => {
            setPub(data);
            setFmt(data.formats[0]);
        }).catch(() => toast.error("Publication not found"));
    }, [id]);

    if (!pub) return <div className="max-w-7xl mx-auto px-4 py-24 font-mono-cap text-xs text-slate-500">Loading…</div>;

    const proceed = () => {
        const cart = [{ publication_id: pub.id, title: pub.title, format: fmt, quantity: qty, unit_price: pub.price_ngn, cover_url: pub.cover_url }];
        sessionStorage.setItem("rj_cart", JSON.stringify(cart));
        navigate("/checkout");
    };

    return (
        <div data-testid={`page-publication-${pub.id}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <Link to="/publications" className="font-mono-cap text-xs text-slate-500 inline-flex items-center gap-2 hover:text-[#c5a059]">
                    <ArrowLeft className="w-3 h-3" /> All Publications
                </Link>
            </div>
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
                <div className="md:col-span-5">
                    <div className="bg-[#f3f1ea] border border-slate-900/10">
                        <img src={pub.cover_url} alt={pub.title} className="w-full h-auto object-cover" />
                    </div>
                </div>
                <div className="md:col-span-7">
                    <div className="font-mono-cap text-xs text-[#c5a059]">{pub.category}</div>
                    <h1 className="font-serif-display text-3xl md:text-5xl text-slate-900 mt-2 leading-tight">
                        {pub.title}
                    </h1>
                    <div className="text-slate-500 mt-2 italic">by {pub.author}</div>
                    <div className="rj-rule-gold w-12 my-6" />
                    <p className="text-slate-700 leading-relaxed">{pub.description}</p>

                    <div className="mt-10 border border-slate-900/10 p-6 bg-[#faf9f6]">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <div className="font-mono-cap text-[10px] text-slate-500">Price</div>
                                <div className="font-serif-display text-3xl text-slate-900">
                                    {formatNGN(pub.price_ngn * qty)}
                                </div>
                            </div>
                            <ShoppingBag className="w-6 h-6 text-[#c5a059]" />
                        </div>

                        <div className="mb-6">
                            <div className="font-mono-cap text-[10px] text-slate-500 mb-2">Format</div>
                            <div className="flex gap-2">
                                {pub.formats.map((f) => (
                                    <button
                                        key={f}
                                        onClick={() => setFmt(f)}
                                        data-testid={`pub-format-${f}`}
                                        className={`px-4 py-2 rounded-sm border text-sm capitalize ${
                                            fmt === f
                                                ? "bg-[#0F172A] text-[#faf9f6] border-[#0F172A]"
                                                : "bg-[#faf9f6] text-slate-800 border-slate-900/15 hover:border-[#c5a059]"
                                        }`}
                                    >
                                        {f === "softcopy" ? "Digital (PDF)" : "Hardcover"}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="mb-6">
                            <div className="font-mono-cap text-[10px] text-slate-500 mb-2">Quantity</div>
                            <div className="inline-flex items-center border border-slate-900/15 rounded-sm">
                                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2" data-testid="pub-qty-decrement">−</button>
                                <span className="px-4 py-2 min-w-[2rem] text-center" data-testid="pub-qty-value">{qty}</span>
                                <button onClick={() => setQty(qty + 1)} className="px-3 py-2" data-testid="pub-qty-increment">+</button>
                            </div>
                        </div>

                        <button
                            onClick={proceed}
                            data-testid="pub-checkout-button"
                            className="w-full bg-[#c5a059] text-[#0F172A] py-3.5 rounded-sm font-medium hover:bg-[#0F172A] hover:text-[#faf9f6] transition-colors"
                        >
                            Proceed to Checkout
                        </button>
                        <p className="mt-3 text-xs text-slate-500">
                            Secure payment via Paystack. Digital downloads delivered immediately on confirmation.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}
